/**
 * Labor Bucket Mapping — the single authority for how the 8 `LaborSubtype`
 * values roll up into the 4 buckets shown in the quotation summary.
 *
 * Before this module there were two independent switch statements doing this
 * job, and they disagreed: `quotationCalculations.ts` sent unmatched subtypes
 * to Engineering, `useAnalytics.ts` sent the same items to Programming. Five of
 * the eight subtypes hit the `default` in both, so the same quote produced
 * different labor breakdowns depending on which screen you were looking at.
 *
 * Two things keep that from coming back, and neither is a comment:
 *
 * 1. `satisfies Record<LaborSubtype, LaborBucket>` below — adding a ninth
 *    subtype fails the build until someone decides where it belongs. There is
 *    no `default` case to silently swallow it.
 * 2. `laborBuckets.test.ts` asserts the four bucket totals sum to the labor
 *    total. A leak anywhere downstream shows up as a failing equality, not as
 *    a bucket that quietly reads 0.
 */

import type { LaborSubtype, LaborBucket } from '../types/common.types';

export type { LaborBucket };

/** Display order for the summary panel. */
export const LABOR_BUCKETS: readonly LaborBucket[] = [
  'engineering',
  'integration',
  'development',
  'installation',
] as const;

/**
 * Subtype → bucket. Exhaustive by construction.
 *
 * Note `commissioning` (הטמעה) rides with installation rather than with
 * development: it is site work, done after the software exists. `testing`
 * (הרצה) is the one that belongs to development.
 */
export const LABOR_SUBTYPE_BUCKET = {
  engineering: 'engineering',
  integration: 'integration',
  development: 'development',
  programming: 'development',
  testing: 'development',
  commissioning: 'installation',
  installation: 'installation',
  support_and_training: 'installation',
} as const satisfies Record<LaborSubtype, LaborBucket>;

/**
 * Where labor lines with no subtype go.
 *
 * This preserves the pre-fix behaviour of `quotationCalculations.ts`, which
 * defaulted to engineering — so untagged rows keep the number they already had
 * rather than moving because of this change. It is a deliberate choice, not a
 * fallthrough: every *known* subtype has an explicit home above.
 */
export const UNCLASSIFIED_LABOR_BUCKET: LaborBucket = 'engineering';

/**
 * Resolve a labor subtype to its summary bucket.
 *
 * Accepts `undefined` (item never tagged) and tolerates strings that are not
 * valid subtypes — rows written by an older schema version reach this from the
 * database untyped, and a runtime `undefined` lookup must not produce `NaN`
 * totals downstream.
 */
export function bucketForLaborSubtype(
  subtype: LaborSubtype | undefined
): LaborBucket {
  if (!subtype) return UNCLASSIFIED_LABOR_BUCKET;
  return LABOR_SUBTYPE_BUCKET[subtype] ?? UNCLASSIFIED_LABOR_BUCKET;
}

// ============ Hebrew Labels ============

/** Bucket headings in the quotation summary. */
export const LABOR_BUCKET_LABELS_HE: Record<LaborBucket, string> = {
  engineering: 'תכנון והנדסה',
  integration: 'אינטגרציה',
  development: 'פיתוח והרצה',
  installation: 'התקנה והדרכה',
};

/**
 * Per-subtype labels, used by the labor-type form, grid and list.
 *
 * These previously lived as three separate copies that had drifted: all three
 * listed only six of the eight subtypes, so `installation` and `programming`
 * were unreachable from the creation form and rendered as raw English
 * elsewhere.
 */
export const LABOR_SUBTYPE_LABELS_HE = {
  engineering: 'תכנון',
  integration: 'אינטגרציה',
  development: 'פיתוח',
  programming: 'תכנות',
  testing: 'הרצה',
  commissioning: 'הטמעה',
  installation: 'התקנה',
  support_and_training: 'תמיכה והדרכה',
} as const satisfies Record<LaborSubtype, string>;

/** All subtypes, grouped for a bucket-aware dropdown. */
export const LABOR_SUBTYPES_BY_BUCKET: Record<LaborBucket, LaborSubtype[]> =
  LABOR_BUCKETS.reduce(
    (acc, bucket) => {
      acc[bucket] = (
        Object.keys(LABOR_SUBTYPE_BUCKET) as LaborSubtype[]
      ).filter(subtype => LABOR_SUBTYPE_BUCKET[subtype] === bucket);
      return acc;
    },
    {} as Record<LaborBucket, LaborSubtype[]>
  );

/**
 * Hebrew label for a subtype, falling back to the raw value for anything the
 * database hands us that is not a known subtype.
 */
export function laborSubtypeLabelHe(subtype: string): string {
  return LABOR_SUBTYPE_LABELS_HE[subtype as LaborSubtype] ?? subtype;
}
